import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import styles from './styles.module.css';
import { StatisticsData } from '../types';
import { useTranslation } from 'react-i18next';

interface DinamicsOfObjectsTableProps {
    data?: StatisticsData[];
}

interface ChartDataItem {
    year: number;
    [key: string]: number; 
}

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        const total = payload.reduce((sum: number, entry: any) => sum + (entry.value || 0), 0);
        return (
            <div className={styles.customTooltip}>
                <p>{`Год: ${label}`}</p>
                <p style={{ fontWeight: 'bold' }}>{`Всего: ${total}`}</p>
                {payload.map((entry: any) => (
                    <p key={entry.name}>{`${entry.name}: ${entry.value}`}</p>
                ))}
            </div>
        );
    }
    return null;
};


const COLOR_PALETTE = [
    
    '#2196F3', // Синий
    '#4CAF50', // Зеленый
    '#FFC107', // Желтый
    '#FF5722', // Оранжевый
    '#9C27B0', // Фиолетовый
    
   
    '#03A9F4', // Голубой
    '#009688', // Бирюзовый
    '#8BC34A', // Светло-зеленый
    '#FFEB3B', // Светло-желтый
    '#FF9800', // Светло-оранжевый
    
   
    '#1976D2', // Темно-синий
    '#388E3C', // Темно-зеленый
    '#FFA000', // Темно-желтый
    '#E64A19', // Темно-оранжевый
    '#7B1FA2', // Темно-фиолетовый
    
  
    '#90CAF9', // Пастельный синий
    '#A5D6A7', // Пастельный зеленый
    '#FFE082', // Пастельный желтый
    '#FFAB91', // Пастельный оранжевый
    '#CE93D8', // Пастельный фиолетовый
];


const PREDEFINED_COLORS: Record<string, string> = {
    'Сельскохозяйственное': '#4CAF50',
    'Промышленное': '#2196F3',
    'Жилое': '#FFC107',
    'Коммерческое': '#FF5722',
    'Социальное': '#9C27B0',
    'Административное': '#009688',
    'Культурное': '#FF9800',
    'Рекреационное': '#8BC34A'
};

const getColorForPurpose = (purpose: string, index: number): string => {
 
    if (PREDEFINED_COLORS[purpose]) {
        return PREDEFINED_COLORS[purpose];
    }
  
    return COLOR_PALETTE[index % COLOR_PALETTE.length];
};

export const DinamicsOfObjectsTable: React.FC<DinamicsOfObjectsTableProps> = ({ data }) => {
    const {t} = useTranslation()
    if (!data || data.length === 0) {
        return null;
    }

    const chartData: ChartDataItem[] = data.map(item => ({
        year: item.year,
        ...item.purposeStatistics
    }));

    const purposes = Object.keys(data[0].purposeStatistics);

    
    const totals = chartData.map(yearData => {
        const total = purposes.reduce((sum, purpose) => sum + (yearData[purpose] || 0), 0);
        return { year: yearData.year, total };
    });

    return (
        <div className={styles.container}>
            <div className={styles.title}>
                <span>{t('Dynamics of objects')}</span>
            </div>
        
            <div className={styles.totalInfo}>
                {totals.map(({ year, total }) => (
                    <div key={year} className={styles.totalItem}>
                        <span>{t('Total number of objects in')} {year} {t('year')}: {total}</span>
                    </div>
                ))}
            </div>
            <div className={styles.chart}>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                        data={chartData}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 15,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="year" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        {purposes.map((purpose, index) => (
                            <Bar
                                key={purpose}
                                dataKey={purpose}
                                name={purpose}
                                fill={getColorForPurpose(purpose, index)}
                                stackId="a"
                                barSize={60}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
